/* eslint-disable react/no-unstable-nested-components, react/prop-types */
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Badge, DataTable, Dropdown, Icon, IconButton, OverlayTrigger, Skeleton, Tooltip,
} from '@openedx/paragon';
import {
  RiBox3Line,
  RiBugLine,
  RiMore2Fill,
  RiQuestionLine,
} from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { useNavigate } from 'react-router-dom';

import {
  formatTicketShortId,
  formatTimeAgo,
  getStatusBadgeColor,
  getStatusLabel,
  getTypeIconColor,
  getTypeLabel,
} from '../utils';
import messages from '../messages';

const TYPE_ICONS = {
  bug: RiBugLine,
  issue: RiQuestionLine,
  feature: RiBox3Line,
  other: RiBox3Line,
};

const getTypeIcon = (type) => TYPE_ICONS[(type || '').toLowerCase()] || RiBox3Line;

const TicketsTable = ({ tickets, loading, onDelete }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const data = useMemo(
    () => tickets.map((ticket) => ({
      id: ticket.id,
      shortId: formatTicketShortId(ticket.id),
      title: ticket.title || '—',
      type: ticket.type || '—',
      status: ticket.status,
      lastUpdated: formatTimeAgo(ticket.updatedAt || ticket.createdAt, intl.locale),
      raw: ticket,
    })),
    [tickets, intl.locale],
  );

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage(messages.columnTicket),
        accessor: 'title',
        Cell: ({ row }) => (
          <div className="learner-support__ticket-cell">
            <span className="learner-support__ticket-title">{row.original.title}</span>
            <span className="learner-support__ticket-id">{row.original.shortId}</span>
          </div>
        ),
      },
      {
        Header: intl.formatMessage(messages.columnType),
        accessor: 'type',
        Cell: ({ row }) => {
          const TypeIcon = getTypeIcon(row.original.type);
          return (
            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip id={`type-${row.original.id}`}>
                  {getTypeLabel(row.original.type, intl, messages)}
                </Tooltip>
              )}
            >
              <span
                className="learner-support__type-icon"
                style={{ color: getTypeIconColor(row.original.type) }}
              >
                <Icon src={TypeIcon} size="sm" />
              </span>
            </OverlayTrigger>
          );
        },
      },
      {
        Header: intl.formatMessage(messages.columnStatus),
        accessor: 'status',
        Cell: ({ row }) => (
          <Badge variant="soft" color={getStatusBadgeColor(row.original.status)}>
            {getStatusLabel(row.original.status, intl, messages)}
          </Badge>
        ),
      },
      {
        Header: intl.formatMessage(messages.columnLastUpdated),
        accessor: 'lastUpdated',
      },
      {
        Header: '',
        accessor: 'id',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Dropdown
            drop="start"
            onToggle={(_, e) => e?.originalEvent?.stopPropagation()}
          >
            <Dropdown.Toggle
              as={IconButton}
              src={RiMore2Fill}
              iconAs={Icon}
              alt={intl.formatMessage(messages.rowMenu)}
              variant="ghost-secondary"
              size="sm"
              id={`ticket-actions-${row.original.id}`}
            />
            <Dropdown.Menu
              renderOnMount
              popperConfig={{ strategy: 'fixed' }}
            >
              <Dropdown.Item onClick={() => navigate(`/support/${row.original.id}`)}>
                {intl.formatMessage(messages.viewTicket)}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onDelete(row.original.raw)}>
                {intl.formatMessage(messages.delete)}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ),
      },
    ],
    [intl, navigate, onDelete],
  );

  if (loading && tickets.length === 0) {
    return <Skeleton height={240} />;
  }

  return (
    <div className="learner-support__table">
      <DataTable
        isSortable
        data={data}
        columns={columns}
        itemCount={data.length}
      >
        <DataTable.Table />
        {!loading && data.length === 0 && (
          <div className="learner-support__empty">
            <h3 className="learner-support__empty-title">
              {intl.formatMessage(messages.emptyTitle)}
            </h3>
            <p className="learner-support__empty-description">
              {intl.formatMessage(messages.emptyDescription)}
            </p>
          </div>
        )}
      </DataTable>
    </div>
  );
};

TicketsTable.propTypes = {
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  loading: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
};

TicketsTable.defaultProps = {
  loading: false,
};

export default TicketsTable;
